import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { AntiMetalButton } from '../components/ui/anti-metal-button';
import { Hero } from '../components/ui/hero-1';
import TheProblem from '../sections/TheProblem';
import './ServicesPage.css';

/* ─── Division data ───────────────────────────────────────── */
const DIVISIONS = [
  {
    id: 'growth',
    label: 'Growth',
    headline: 'Build. Acquire. Convert. Scale.',
    description:
      'Web development, Shopify, CRO, paid media, SEO and growth systems designed to turn attention into action and attention into revenue.',
    route: '/growth',
    accent: '#ec2b28',
    gradientFrom: '#6b0a0a',
    gradientTo: '#1a0505',
    glowStop: 'rgba(236,43,40,0.85)',
  },
  {
    id: 'intelligence',
    label: 'Intelligence',
    headline: 'Turn data into direction.',
    description:
      'Analytics, dashboards and business intelligence that turn fragmented data into crystal-clear commercial decisions.',
    route: '/intelligence',
    accent: '#3b82f6',
    gradientFrom: '#0a1b4a',
    gradientTo: '#050d1a',
    glowStop: 'rgba(59,130,246,0.85)',
  },
  {
    id: 'ai',
    label: 'AI',
    headline: 'AI systems built for your workflow.',
    description:
      'AI agents, automations and custom solutions designed around how your business actually operates — not generic templates.',
    route: '/aurelix-ai',
    accent: '#a855f7',
    gradientFrom: '#2e0a52',
    gradientTo: '#0f0318',
    glowStop: 'rgba(168,85,247,0.85)',
  },
];

/* ─── Single card ─────────────────────────────────────────── */
function DivisionCard({ div, index }) {
  const navigate = useNavigate();

  return (
    <motion.div
      className="sp2-card"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.52, delay: index * 0.08 }}
      onClick={() => div.route && navigate(div.route)}
      style={{ cursor: div.route ? 'pointer' : 'default' }}
    >
      {/* Gradient blob thumbnail */}
      <div
        className="sp2-thumb"
        style={{
          background: `radial-gradient(ellipse 75% 60% at 50% 0%, ${div.glowStop} 0%, ${div.gradientFrom} 45%, ${div.gradientTo} 100%)`,
        }}
      >
        <div className="sp2-thumb-noise" />
        <div className="sp2-thumb-brand">
          <span className="sp2-thumb-aurelix">Aurelix</span>
          <span className="sp2-thumb-label" style={{ color: div.accent }}>
            {div.label}
          </span>
        </div>
      </div>

      {/* Card body */}
      <div className="sp2-body">
        <h3 className="sp2-title">{div.headline}</h3>
        <p className="sp2-desc">{div.description}</p>

        <div className="sp2-footer">
          <button
            className="sp2-btn"
            style={{ color: div.accent, borderColor: `${div.accent}55` }}
            onClick={(e) => {
              e.stopPropagation();
              if (div.route) navigate(div.route);
            }}
          >
            <span>{div.route ? 'Explore More' : 'Coming Soon'}</span>
            {div.route && <ArrowUpRight size={14} strokeWidth={2.5} />}
          </button>
        </div>
      </div>
    </motion.div>
  );
}

/* ─── Page ────────────────────────────────────────────────── */
export default function ServicesPage() {
  const navigate = useNavigate();

  return (
    <>
      <Navbar />
      <main className="sp-main">

        {/* Hero */}
        <Hero
          eyebrow="WHAT WE DO"
          title="The capabilities behind better growth."
          subtitle="Aurelix brings together growth, commerce, intelligence and AI to help businesses build, improve and scale the systems that move them forward."
          description="Whether the challenge is acquiring more customers, improving conversion, expanding across commerce channels, making better decisions with data or implementing AI—we bring together the right capabilities around the problem."
          ctaLabel="Explore our capabilities ↓"
          ctaHref="#divisions"
        />

        <TheProblem />

        {/* ── Divisions: same card style as home page ── */}
        <section className="sp2-section" id="divisions">
          <div className="sp2-header">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="sp2-eyebrow">
                <span className="sp2-eyebrow-dot" />
                <span>Our Divisions</span>
              </div>
              <h2 className="sp2-heading">
                Three capabilities.<br />
                <span className="sp2-heading-grad">One connected system.</span>
              </h2>
              <p className="sp2-subhead">
                Each division is built to work standalone — and compounds when combined.
              </p>
            </motion.div>
          </div>

          <div className="sp2-grid">
            {DIVISIONS.map((div, i) => (
              <DivisionCard key={div.id} div={div} index={i} />
            ))}
          </div>

          {/* CTA row */}
          <motion.div
            className="sp2-cta-row"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <div className="sp2-cta-line" />
            <AntiMetalButton as="a" href="https://calendly.com/aurelixlabs/30min" target="_blank" rel="noopener noreferrer">
              Book a Strategy Session
            </AntiMetalButton>
            <div className="sp2-cta-line sp2-cta-line--r" />
          </motion.div>
        </section>

      </main>
      <Footer />
    </>
  );
}
